import { useMemo, useState } from 'react';
import { CYLINDO_ACCOUNT_ID } from 'commons/constants';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';

const CYLINDO_CONTENT_API = 'https://content.cylindo.com/api/v2';

const useCylindoDimensionShots = ({ upholsteryKey } = {}) => {
  const [selectedDimensionShotIndex, setSelectedDimensionShotIndex] = useState(
    0
  );

  const { data: basicDetails } = usePDPBasicDetails();

  const productId = basicDetails?.id;
  const cylindoDimensionCode =
    basicDetails?.custom_properties?.cylindo_dimension_code;
  const isCylindoProduct = !!basicDetails?.custom_properties?.cylindo_product;
  const dimensionalImageDesktop =
    basicDetails?.custom_properties?.dimensional_image_desktop;
  const dimensionalImageMobile =
    basicDetails?.custom_properties?.dimensional_image_mobile;

  // Parse dimension codes from comma-separated string
  const dimensionCodes = useMemo(() => {
    if (!cylindoDimensionCode || typeof cylindoDimensionCode !== 'string') {
      return [];
    }
    return cylindoDimensionCode
      .split(',')
      .map(code => code.trim())
      .filter(Boolean);
  }, [cylindoDimensionCode]);

  const hasCylindoDimensionShots =
    isCylindoProduct && dimensionCodes.length > 0;

  // Generate dimension shot URLs
  const dimensionShotUrls = useMemo(() => {
    if (
      !hasCylindoDimensionShots ||
      !productId ||
      dimensionCodes.length === 0
    ) {
      return [];
    }

    const dimensionShots = dimensionCodes.map((dimensionCode, index) => {
      const size = 1024;
      const baseUrl = `${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/dimensions/${productId}.png?size=${size}&dimensionCode=${dimensionCode}`;
      const url = upholsteryKey
        ? `${baseUrl}&feature=UPHOLSTERY:${encodeURIComponent(upholsteryKey)}`
        : baseUrl;

      return {
        id: `dimension-${dimensionCode}`,
        src: url,
        baseUrl,
        dimensionCode,
        index,
      };
    });
    return dimensionShots;
  }, [hasCylindoDimensionShots, productId, dimensionCodes, upholsteryKey]);

  const shouldShowCylindoDimensionShots = hasCylindoDimensionShots;
  const shouldShowStaticDimensionImages =
    !hasCylindoDimensionShots &&
    (dimensionalImageDesktop || dimensionalImageMobile);
  const shouldShowDimensionThumbnail =
    shouldShowCylindoDimensionShots || shouldShowStaticDimensionImages;

  const currentDimensionShot =
    dimensionShotUrls[selectedDimensionShotIndex] || null;

  const goToNextDimensionShot = () => {
    if (dimensionShotUrls.length > 1) {
      setSelectedDimensionShotIndex(prev =>
        prev >= dimensionShotUrls.length - 1 ? 0 : prev + 1
      );
    }
  };

  const goToPreviousDimensionShot = () => {
    if (dimensionShotUrls.length > 1) {
      setSelectedDimensionShotIndex(prev =>
        prev <= 0 ? dimensionShotUrls.length - 1 : prev - 1
      );
    }
  };

  const returnValue = {
    dimensionShotUrls,
    currentDimensionShot,
    selectedDimensionShotIndex,
    hasCylindoDimensionShots,
    hasMultipleDimensionShots: dimensionShotUrls.length > 1,
    dimensionalImageDesktop,
    dimensionalImageMobile,
    shouldShowCylindoDimensionShots,
    shouldShowStaticDimensionImages,
    shouldShowDimensionThumbnail,
    setSelectedDimensionShotIndex,
    goToNextDimensionShot,
    goToPreviousDimensionShot,
  };

  return returnValue;
};

export default useCylindoDimensionShots;
